/* ------------------------------------------------------------------ *
 * features/dashboard/HeadInsightDetail.tsx
 *
 * The drill-down list behind a Principal "At a glance" stat. Tapping a
 * headline number on the Head dashboard lands HERE, on the exact list
 * it refers to — never on a screen she has to reconfigure. One screen,
 * a `focus` prop selects which list:
 *
 *   atRisk        — every at-risk pupil, school-wide (Al-Nur rule)
 *   topClasses    — full class ranking by average
 *   needsAttention— class-subjects sitting below the pass mark
 *   absentToday   — today's absentees / lates across all registers
 *
 * Large lists (esp. school-wide at-risk) get filters (search / class /
 * tier) and show the first 20 with a "view more" expander. Rows drill
 * through to the student profile where that makes sense. Data comes
 * from useHeadInsights (the same figures the tiles show).
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, AlertTriangle, TrendingUp, TrendingDown, CalendarX, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { useHeadInsights } from './useHeadInsights';
import type { TabId } from '@/components/Shell';

export type HeadFocus = 'atRisk' | 'topClasses' | 'needsAttention' | 'absentToday';

type Props = {
  focus: HeadFocus;
  year: string;
  term: string;
  onNavigate: (t: TabId, opts?: { studentId?: string }) => void;
};

const PAGE = 15;

const META: Record<HeadFocus, { title: string; sub: string; icon: typeof AlertTriangle; badge: string }> = {
  atRisk: { title: 'Pupils at risk', sub: 'Every pupil flagged this term — critical first', icon: AlertTriangle, badge: 'bg-red-500' },
  topClasses: { title: 'Class ranking', sub: 'All classes by average mark, best to weakest', icon: TrendingUp, badge: 'bg-emerald-600' },
  needsAttention: { title: 'Needs attention', sub: 'Class-subjects averaging below the pass mark', icon: TrendingDown, badge: 'bg-amber-500' },
  absentToday: { title: 'Absent today', sub: "Pupils marked absent or late in today's registers", icon: CalendarX, badge: 'bg-red-500' },
};

function TierPill({ tier }: { tier: 'critical' | 'borderline' }) {
  return (
    <span
      className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
        tier === 'critical'
          ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
          : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
      }`}
    >
      {tier === 'critical' ? 'Critical' : 'Borderline'}
    </span>
  );
}

const selectCls =
  'rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white';

export default function HeadInsightDetail({ focus, year, term, onNavigate }: Props) {
  const { loading, lists } = useHeadInsights(year, term);
  const meta = META[focus];
  const Icon = meta.icon;

  const [search, setSearch] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [tierFilter, setTierFilter] = useState<'all' | 'critical' | 'borderline'>('all');
  const [expanded, setExpanded] = useState(false);

  // Reset filters + pagination whenever the stat we're drilling into changes.
  useEffect(() => {
    setSearch('');
    setClassFilter('');
    setTierFilter('all');
    setExpanded(false);
  }, [focus]);

  const q = search.trim().toLowerCase();
  const showClassFilter = focus === 'atRisk' || focus === 'absentToday' || focus === 'needsAttention';
  const showTierFilter = focus === 'atRisk';

  const classOptions = useMemo(() => {
    const set = new Set<string>();
    if (focus === 'atRisk') lists.atRisk.forEach((s) => set.add(s.className));
    else if (focus === 'absentToday') lists.absentToday.forEach((a) => set.add(a.className));
    else if (focus === 'needsAttention') lists.subjectLows.forEach((s) => set.add(s.className));
    return [...set].sort();
  }, [focus, lists]);

  const atRisk = useMemo(
    () =>
      lists.atRisk.filter(
        (s) =>
          (tierFilter === 'all' || s.tier === tierFilter) &&
          (!classFilter || s.className === classFilter) &&
          (!q || s.fullName.toLowerCase().includes(q)),
      ),
    [lists.atRisk, tierFilter, classFilter, q],
  );
  const classesRanked = useMemo(
    () => lists.classesRanked.filter((c) => !q || c.name.toLowerCase().includes(q)),
    [lists.classesRanked, q],
  );
  const subjectLows = useMemo(
    () =>
      lists.subjectLows.filter(
        (s) =>
          (!classFilter || s.className === classFilter) &&
          (!q || `${s.className} ${s.subjectName}`.toLowerCase().includes(q)),
      ),
    [lists.subjectLows, classFilter, q],
  );
  const absentToday = useMemo(
    () =>
      lists.absentToday.filter(
        (a) => (!classFilter || a.className === classFilter) && (!q || a.name.toLowerCase().includes(q)),
      ),
    [lists.absentToday, classFilter, q],
  );

  const total =
    focus === 'atRisk' ? atRisk.length
    : focus === 'topClasses' ? classesRanked.length
    : focus === 'needsAttention' ? subjectLows.length
    : absentToday.length;
  const limit = expanded ? total : PAGE;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-[#1b3a6b] to-[#2d5295] text-white p-6">
        <button
          onClick={() => onNavigate('home')}
          className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-3"
        >
          <ArrowLeft size={16} /> Back to dashboard
        </button>
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${meta.badge}`}>
            <Icon size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              {meta.title}
              {!loading && <span className="text-white/70 text-base font-semibold">· {total}</span>}
            </h1>
            <p className="text-sm opacity-90">{meta.sub}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      {!loading && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={focus === 'topClasses' || focus === 'needsAttention' ? 'Search class or subject…' : 'Search by name…'}
              className={`${selectCls} w-full pl-9`}
            />
          </div>
          {showClassFilter && classOptions.length > 0 && (
            <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} className={selectCls}>
              <option value="">All classes</option>
              {classOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {showTierFilter && (
            <select value={tierFilter} onChange={(e) => setTierFilter(e.target.value as typeof tierFilter)} className={selectCls}>
              <option value="all">All tiers</option>
              <option value="critical">Critical only</option>
              <option value="borderline">Borderline only</option>
            </select>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 px-1">Loading…</p>
      ) : total === 0 ? (
        <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/40 p-8 text-center">
          <TrendingUp size={28} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {search || classFilter || tierFilter !== 'all'
              ? 'No matches for these filters.'
              : focus === 'absentToday'
                ? 'No absentees recorded in today’s registers.'
                : focus === 'needsAttention'
                  ? 'Nothing is sitting below the pass mark.'
                  : 'Nothing to show for this term yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-2xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800/40 overflow-hidden divide-y divide-gray-100 dark:divide-gray-700/60">
            {/* AT RISK */}
            {focus === 'atRisk' &&
              atRisk.slice(0, limit).map((s) => (
                <button
                  key={s.studentId}
                  onClick={() => onNavigate('studentProfile', { studentId: s.studentId })}
                  className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2.5">
                      <span className="font-bold text-base text-gray-900 dark:text-white truncate">{s.fullName}</span>
                      <TierPill tier={s.tier} />
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {s.className}
                      {s.failed.length > 0 && <span className="text-red-600 dark:text-red-300"> · failing {s.failed.join(', ')}</span>}
                      {s.failed.length === 0 && s.borderline.length > 0 && <span> · borderline {s.borderline.join(', ')}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-extrabold text-gray-800 dark:text-gray-100 leading-none">{s.average ?? '—'}%</div>
                    <div className="text-xs text-gray-400 mt-1">average</div>
                  </div>
                </button>
              ))}

            {/* CLASS RANKING */}
            {focus === 'topClasses' &&
              classesRanked.slice(0, limit).map((c, i) => (
                <div key={c.classId} className="flex items-center gap-4 px-5 py-4">
                  <span className="w-8 text-center text-base font-bold text-gray-400 shrink-0">{i + 1}</span>
                  <span className="w-24 font-bold text-base text-gray-900 dark:text-white shrink-0">{c.name}</span>
                  <div className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${c.average >= c.passMark ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.max(0, Math.min(100, c.average))}%` }}
                    />
                  </div>
                  <span className={`w-14 text-right text-lg font-extrabold ${c.average >= c.passMark ? 'text-emerald-600 dark:text-emerald-300' : 'text-amber-600 dark:text-amber-300'}`}>
                    {Math.round(c.average)}%
                  </span>
                </div>
              ))}

            {/* NEEDS ATTENTION */}
            {focus === 'needsAttention' &&
              subjectLows.slice(0, limit).map((s) => (
                <div key={s.key} className="flex items-center justify-between gap-4 px-5 py-4">
                  <div className="min-w-0">
                    <p className="font-bold text-base text-gray-900 dark:text-white truncate">{s.className} · {s.subjectName}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">pass mark {s.passMark}%</p>
                  </div>
                  <span className="inline-flex items-center text-base font-extrabold px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800 shrink-0">
                    {Math.round(s.avg)}%
                  </span>
                </div>
              ))}

            {/* ABSENT TODAY */}
            {focus === 'absentToday' &&
              absentToday.slice(0, limit).map((a) => (
                <button
                  key={`${a.studentId}-${a.className}`}
                  onClick={() => onNavigate('studentProfile', { studentId: a.studentId })}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40"
                >
                  <div className="min-w-0">
                    <p className="font-bold text-base text-gray-900 dark:text-white truncate">{a.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{a.className}</p>
                  </div>
                  <span
                    className={`inline-flex items-center text-sm font-semibold px-3 py-1 rounded-full border shrink-0 ${
                      a.status === 'late'
                        ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
                        : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                    }`}
                  >
                    {a.status === 'late' ? 'Late' : a.status === 'sick' ? 'Sick' : 'Absent'}
                  </span>
                </button>
              ))}
          </div>

          {/* View more / less */}
          {total > PAGE && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40 text-base font-semibold text-[#1b3a6b] dark:text-blue-300 hover:bg-gray-50 dark:hover:bg-gray-700/40"
            >
              {expanded ? (
                <>Show fewer <ChevronUp size={16} /></>
              ) : (
                <>View more — {total - PAGE} more <ChevronDown size={16} /></>
              )}
            </button>
          )}
        </>
      )}
    </div>
  );
}
